/**
 * PRD.290 / 292 / 293 — Unified, patient-contextual New Pharmacy Sale drawer.
 *
 * Flow (one right Billing-width drawer; prescription reference is a separate
 * LEFT non-modal panel):
 *   1. Search — existing patient search + today's uninvoiced prescriptions.
 *   2. Choose — once a patient is picked, the list is REPLACED by that patient's
 *      eligible prescriptions ("Patient Prescriptions"). Exactly one → auto-
 *      selected; several → pick one; none → continue with a manual sale.
 *   3. Billing — the existing Billing surface, with a compact Patient +
 *      Prescription summary above it and (from a prescription) the reused
 *      "Add Items from Prescription" picker fed this encounter's lines.
 *
 * No writes on patient/prescription selection; no automatic invoice creation;
 * never navigates to `/patient/:id`. Reuses the existing patient search, the
 * today's-prescriptions query (filtered client-side per patient — no new
 * backend), the read-only reference panel, Billing and the payment flow.
 */
import { useEffect, useMemo, useState, type CSSProperties } from 'react';
import { Drawer } from '@helios/ui/overlay';
import { useAuth } from '@helios/auth-firebase-web';
import {
  qPatientsDrawerList,
  qPatientPrescriptions,
  qTodaysPrescriptions,
  type PatientDrawerRow,
  type TodaysPrescriptionRow,
  type TreatmentRecordDrawerWidth,
} from '@helios/data-access';
import { usePowerSyncQuery } from '../../adapters/powersync';
import { NavIcon, useBrandTokens } from '../../shell/shared';
import { BillingInvoicesPanel } from '../patient/encounters/TreatmentInvoiceDrawer';
import { DRAWER_WIDTH_BY_PRESET } from '../patient/encounters/TreatmentChartingFilesDrawer';
import { PharmacyPrescriptionReference } from './PharmacyPrescriptionReference';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  drawerWidth: TreatmentRecordDrawerWidth;
}

interface SalePatient {
  id: string;
  name: string;
  number: string | null;
}
interface RxRef {
  encounterId: string;
  patientId: string;
  patientName: string | null;
}

function useDebouncedValue(value: string, delay: number): string {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}

function localTodayIso(): string {
  const d = new Date();
  const off = d.getTimezoneOffset();
  return new Date(d.getTime() - off * 60000).toISOString().slice(0, 10);
}

// "Aug 1, 2026 12:15 · Dr. X · 3 medicines"
function rxMetaLine(r: TodaysPrescriptionRow): string {
  const date = r.prescription_date ? r.prescription_date.replace('T', ' ').slice(0, 16) : '—';
  const doc = r.provider_name ? `Dr. ${r.provider_name}` : null;
  const meds = `${r.total_meds} medicine${r.total_meds === 1 ? '' : 's'}`;
  return [date, doc, meds].filter(Boolean).join(' · ');
}

// A prescription is eligible to bill while any billable line isn't yet paid.
function isEligible(r: TodaysPrescriptionRow): boolean {
  return (r.billable_meds ?? 0) > (r.paid_meds ?? 0);
}

// Status hint (reuses the Today's Prescriptions billing/paid counts). Served
// (fully-paid) prescriptions are surfaced too so they aren't reported missing.
function rxStatus(r: TodaysPrescriptionRow): string | null {
  const toBill = (r.billable_meds ?? 0) - (r.invoiced_meds ?? 0);
  const unpaid = (r.invoiced_meds ?? 0) - (r.paid_meds ?? 0);
  if (toBill > 0) return `${toBill} to bill`;
  if (unpaid > 0) return 'Awaiting payment';
  if ((r.billable_meds ?? 0) > 0) return 'Fully invoiced';
  return 'No billable medicines';
}

export function NewPharmacySaleDrawer({ open, onOpenChange, drawerWidth }: Props) {
  const brandTokens = useBrandTokens();
  const { user } = useAuth();
  const userId = user?.uid ?? '';
  const todayIso = useMemo(() => localTodayIso(), []);

  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search, 300);
  const [focused, setFocused] = useState(false);
  const [step, setStep] = useState<'search' | 'choose' | 'billing'>('search');
  const [patient, setPatient] = useState<SalePatient | null>(null);
  const [prescription, setPrescription] = useState<TodaysPrescriptionRow | null>(null);
  // "View" previews a prescription in the LEFT reference panel.
  const [previewRx, setPreviewRx] = useState<RxRef | null>(null);
  const [rxCollapsed, setRxCollapsed] = useState(false);

  const { data: patientRows = [] } = usePowerSyncQuery<PatientDrawerRow>(
    qPatientsDrawerList({ userId, search: debouncedSearch, tab: 'all' }),
  );
  const { data: todaysRxRows = [] } = usePowerSyncQuery<TodaysPrescriptionRow>(
    qTodaysPrescriptions(todayIso),
  );
  // PRD.293 — ALL of the selected patient's prescriptions (any date, incl.
  // served) so we can section them rather than hide served ones.
  const { data: patientAllRx = [] } = usePowerSyncQuery<TodaysPrescriptionRow>(
    qPatientPrescriptions(patient?.id ?? ''),
  );

  useEffect(() => {
    if (!open) {
      setSearch('');
      setFocused(false);
      setStep('search');
      setPatient(null);
      setPrescription(null);
      setPreviewRx(null);
      setRxCollapsed(false);
    }
  }, [open]);

  const showResults = step === 'search' && (focused || search.trim().length > 0);

  // Section the patient's prescriptions: today (billable), earlier (billable,
  // other dates), and already-served (fully invoiced/paid, any date).
  const sections = useMemo(() => {
    const today: TodaysPrescriptionRow[] = [];
    const earlier: TodaysPrescriptionRow[] = [];
    const served: TodaysPrescriptionRow[] = [];
    for (const r of patientAllRx) {
      if (!isEligible(r)) served.push(r);
      else if ((r.prescription_date ?? '').slice(0, 10) === todayIso) today.push(r);
      else earlier.push(r);
    }
    return { today, earlier, served };
  }, [patientAllRx, todayIso]);

  // ── Transitions (no writes) ──
  const choosePatient = (p: PatientDrawerRow) => {
    const list = todaysRxRows.filter((r) => r.patient_id === p.id);
    setPatient({ id: p.id, name: p.full_name ?? 'Unknown', number: p.patient_number });
    setPreviewRx(null);
    if (list.length === 1) {
      // Exactly one eligible prescription → auto-select, skip the extra step.
      setPrescription(list[0]);
      setStep('billing');
    } else {
      setPrescription(null);
      setStep('choose');
    }
  };

  const handleUsePrescription = (r: TodaysPrescriptionRow) => {
    if (!r.patient_id) return;
    setPatient(
      (prev) => prev ?? { id: r.patient_id!, name: r.patient_name ?? 'Unknown', number: null },
    );
    setPreviewRx(null);
    setPrescription(r);
    setStep('billing');
  };

  const continueManual = () => {
    setPrescription(null);
    setPreviewRx(null);
    setStep('billing');
  };

  const changePatient = () => {
    setPatient(null);
    setPrescription(null);
    setPreviewRx(null);
    setStep('search');
  };

  const clearPrescription = () => {
    setPrescription(null);
    setStep('choose');
  };

  const toggleView = (r: TodaysPrescriptionRow) => {
    if (!r.patient_id) return;
    setRxCollapsed(false);
    setPreviewRx((prev) =>
      prev?.encounterId === r.encounter_id
        ? null
        : { encounterId: r.encounter_id, patientId: r.patient_id!, patientName: r.patient_name },
    );
  };

  // Which prescription the LEFT reference panel should show.
  const leftRx: RxRef | null =
    step === 'billing' && prescription && prescription.patient_id
      ? {
          encounterId: prescription.encounter_id,
          patientId: prescription.patient_id,
          patientName: prescription.patient_name,
        }
      : previewRx;

  return (
    <>
      <Drawer
        open={open}
        onOpenChange={onOpenChange}
        title="New Pharmacy Sale"
        description={
          step === 'billing'
            ? `Billing — ${patient?.name ?? ''}`
            : step === 'choose'
              ? 'Choose a prescription, or continue with a manual sale.'
              : 'Search a patient or pick a prescription to begin.'
        }
        portalStyle={brandTokens as CSSProperties}
        width={DRAWER_WIDTH_BY_PRESET[drawerWidth]}
      >
        {step === 'search' ? (
          <div style={scrollColStyle}>
            {/* Section A — Patient search */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <label style={labelStyle}>Patient</label>
              <div style={{ position: 'relative' }}>
                <div style={searchIconStyle}>
                  <NavIcon name="search" />
                </div>
                <input
                  type="text"
                  placeholder="Search patient name, Patient No., ID No..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onFocus={() => setFocused(true)}
                  onBlur={() => setTimeout(() => setFocused(false), 150)}
                  style={{ ...inputStyle, paddingLeft: 40 }}
                />
              </div>

              {showResults && (
                <div style={resultsBoxStyle}>
                  {patientRows.length === 0 ? (
                    <div style={emptyRowStyle}>No patients found</div>
                  ) : (
                    patientRows.map((p) => (
                      <button
                        key={p.id}
                        type="button"
                        onClick={() => choosePatient(p)}
                        style={resultRowStyle}
                      >
                        <div style={{ minWidth: 0, flex: 1 }}>
                          <div style={rowTitleStyle}>{p.full_name ?? 'Unknown'}</div>
                          {(p.phone || p.email) && (
                            <div style={rowSubStyle}>
                              {[p.phone, p.email].filter(Boolean).join(' · ')}
                            </div>
                          )}
                        </div>
                        {p.patient_number && (
                          <span
                            style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}
                          >
                            P-{p.patient_number}
                          </span>
                        )}
                      </button>
                    ))
                  )}
                </div>
              )}

              <span style={hintStyle}>
                Existing patients only. Selecting a patient does not create an invoice.
              </span>
            </div>

            {/* Section B — Today's uninvoiced prescriptions (all patients) */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <label style={labelStyle}>Today's Prescriptions</label>
              {todaysRxRows.length === 0 ? (
                <div style={dashedEmptyStyle}>No prescriptions waiting to be billed today.</div>
              ) : (
                <div style={listBoxStyle}>
                  {todaysRxRows.map((r) => (
                    <PrescriptionRow
                      key={r.encounter_id}
                      r={r}
                      showPatient
                      previewing={previewRx?.encounterId === r.encounter_id}
                      onView={() => toggleView(r)}
                      onUse={() => handleUsePrescription(r)}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        ) : step === 'choose' ? (
          <div style={scrollColStyle}>
            <PatientChip patient={patient} onChange={changePatient} />

            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-4)' }}>
              <label style={labelStyle}>Patient Prescriptions</label>

              {patientAllRx.length === 0 ? (
                <div style={dashedEmptyStyle}>
                  <div style={{ color: 'var(--text)', fontWeight: 'var(--font-weight-medium)' }}>
                    No prescriptions found for this patient.
                  </div>
                  <div style={{ marginTop: 4 }}>Continue with a manual pharmacy sale.</div>
                  <div style={{ marginTop: 'var(--sp-3)' }}>
                    <button type="button" onClick={continueManual} style={primaryBtnStyle}>
                      Continue to Billing
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <RxSection
                    title="Today"
                    rows={sections.today}
                    eligible
                    previewId={previewRx?.encounterId}
                    onView={toggleView}
                    onUse={handleUsePrescription}
                  />
                  <RxSection
                    title="Earlier — not yet billed"
                    rows={sections.earlier}
                    eligible
                    previewId={previewRx?.encounterId}
                    onView={toggleView}
                    onUse={handleUsePrescription}
                  />
                  <RxSection
                    title="Already served"
                    rows={sections.served}
                    eligible={false}
                    previewId={previewRx?.encounterId}
                    onView={toggleView}
                    onUse={handleUsePrescription}
                  />
                  <button
                    type="button"
                    onClick={continueManual}
                    style={{
                      alignSelf: 'flex-start',
                      background: 'none',
                      border: 'none',
                      color: 'var(--color-primary)',
                      fontSize: 'var(--font-size-sm)',
                      fontWeight: 'var(--font-weight-medium)',
                      cursor: 'pointer',
                      padding: '4px 0',
                    }}
                  >
                    Continue without a prescription
                  </button>
                </>
              )}
            </div>
          </div>
        ) : (
          // ── Billing ──
          <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
            <div style={summaryBarStyle}>
              {/* Patient */}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 'var(--sp-3)',
                }}
              >
                <div style={{ minWidth: 0 }}>
                  <div style={summaryLabelStyle}>Patient</div>
                  <div style={summaryValueStyle}>
                    {patient?.name}
                    {patient?.number ? ` · P-${patient.number}` : ''}
                  </div>
                </div>
                <button type="button" onClick={changePatient} style={secondaryBtnStyle}>
                  Change
                </button>
              </div>

              {/* Prescription (compact summary) */}
              <div style={{ marginTop: 'var(--sp-3)' }}>
                <div style={summaryLabelStyle}>Prescription</div>
                {prescription ? (
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 'var(--sp-3)',
                    }}
                  >
                    <div style={summaryValueStyle}>{rxMetaLine(prescription)}</div>
                    <div style={{ display: 'inline-flex', gap: 6, flexShrink: 0 }}>
                      <button
                        type="button"
                        onClick={() => setRxCollapsed(false)}
                        style={secondaryBtnStyle}
                      >
                        View Prescription
                      </button>
                      <button type="button" onClick={clearPrescription} style={secondaryBtnStyle}>
                        Clear
                      </button>
                    </div>
                  </div>
                ) : (
                  <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--text-muted)' }}>
                    Manual sale — no prescription.
                  </div>
                )}
              </div>
            </div>

            <div style={{ flex: 1, minWidth: 0, minHeight: 0 }}>
              {/* No autoAddPrescriptionIds → Billing never auto-creates an
                  invoice. From a prescription, the "Add Items from Prescription"
                  picker is fed this encounter's lines (closed-encounter-safe). */}
              <BillingInvoicesPanel
                active={open}
                patientId={patient?.id ?? ''}
                initialEncounterId={prescription?.encounter_id ?? null}
                initialDate={
                  prescription?.prescription_date
                    ? prescription.prescription_date.slice(0, 10)
                    : todayIso
                }
                pharmacyPrescriptionEncounterId={prescription?.encounter_id ?? undefined}
              />
            </div>
          </div>
        )}
      </Drawer>

      {/* PRD.292 — LEFT-docked, non-modal prescription reference (own portal). */}
      {open && leftRx && (
        <PharmacyPrescriptionReference
          patientId={leftRx.patientId}
          encounterId={leftRx.encounterId}
          patientName={leftRx.patientName}
          collapsed={rxCollapsed}
          onToggleCollapsed={() => setRxCollapsed((v) => !v)}
        />
      )}
    </>
  );
}

// ─── Sectioned list of a patient's prescriptions ───

function RxSection({
  title,
  rows,
  eligible,
  previewId,
  onView,
  onUse,
}: {
  title: string;
  rows: TodaysPrescriptionRow[];
  /** Eligible sections get "Use Prescription"; served ones are View-only. */
  eligible: boolean;
  previewId: string | undefined;
  onView: (r: TodaysPrescriptionRow) => void;
  onUse: (r: TodaysPrescriptionRow) => void;
}) {
  if (rows.length === 0) return null;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <div
        style={{
          fontSize: 'var(--font-size-xs)',
          fontWeight: 'var(--font-weight-semi)',
          color: 'var(--text-muted)',
          textTransform: 'uppercase',
          letterSpacing: '0.04em',
        }}
      >
        {title} · {rows.length}
      </div>
      <div style={listBoxStyle}>
        {rows.map((r) => (
          <PrescriptionRow
            key={r.encounter_id}
            r={r}
            eligible={eligible}
            previewing={previewId === r.encounter_id}
            onView={() => onView(r)}
            onUse={() => onUse(r)}
          />
        ))}
      </div>
    </div>
  );
}

// ─── Shared row for a prescription (search list / patient list) ───

function PrescriptionRow({
  r,
  showPatient,
  eligible = true,
  previewing,
  onView,
  onUse,
}: {
  r: TodaysPrescriptionRow;
  showPatient?: boolean;
  eligible?: boolean;
  previewing: boolean;
  onView: () => void;
  onUse: () => void;
}) {
  const status = rxStatus(r);
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--sp-3)',
        padding: 'var(--sp-3)',
        borderBottom: '1px solid var(--border)',
        background: previewing ? 'var(--bg-primary-subtle)' : undefined,
        opacity: eligible ? 1 : 0.75,
      }}
    >
      <div style={{ minWidth: 0, flex: 1 }}>
        {showPatient && <div style={rowTitleStyle}>{r.patient_name ?? '—'}</div>}
        <div style={showPatient ? rowSubStyle : { ...rowSubStyle, color: 'var(--text)' }}>
          {rxMetaLine(r)}
        </div>
        {status && (
          <div
            style={{
              fontSize: 'var(--font-size-xs)',
              color: eligible ? 'var(--text-muted)' : 'var(--color-success-text, #166534)',
              marginTop: 1,
            }}
          >
            {status}
          </div>
        )}
      </div>
      <button
        type="button"
        onClick={onView}
        disabled={!r.patient_id}
        title="Preview prescription in the reference panel"
        style={
          previewing
            ? {
                ...secondaryBtnStyle,
                borderColor: 'var(--color-primary)',
                color: 'var(--color-primary)',
              }
            : secondaryBtnStyle
        }
      >
        {previewing ? 'Viewing' : 'View'}
      </button>
      {eligible && (
        <button type="button" onClick={onUse} disabled={!r.patient_id} style={primaryBtnStyle}>
          Use Prescription
        </button>
      )}
    </div>
  );
}

function PatientChip({ patient, onChange }: { patient: SalePatient | null; onChange: () => void }) {
  return (
    <div style={summaryBarStyle}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 'var(--sp-3)',
        }}
      >
        <div style={{ minWidth: 0 }}>
          <div style={summaryLabelStyle}>Patient</div>
          <div style={summaryValueStyle}>
            {patient?.name}
            {patient?.number ? ` · P-${patient.number}` : ''}
          </div>
        </div>
        <button type="button" onClick={onChange} style={secondaryBtnStyle}>
          Change
        </button>
      </div>
    </div>
  );
}

// ─── Styles ───

const scrollColStyle: CSSProperties = {
  height: '100%',
  minHeight: 0,
  overflowY: 'auto',
  padding: 'var(--sp-5)',
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--sp-6)',
};

const labelStyle: CSSProperties = {
  fontSize: 'var(--font-size-sm)',
  fontWeight: 'var(--font-weight-medium)',
  color: 'var(--text)',
};

const inputStyle: CSSProperties = {
  width: '100%',
  height: 'var(--input-height, 40px)',
  padding: '0 var(--input-padding-x, 14px)',
  borderRadius: 'var(--input-radius, 10px)',
  border: '1px solid var(--border-input)',
  background: 'var(--input-bg)',
  fontSize: 'var(--font-size-sm)',
  color: 'var(--input-text)',
  outline: 'none',
  boxSizing: 'border-box',
  fontFamily: 'inherit',
};

const searchIconStyle: CSSProperties = {
  position: 'absolute',
  left: 12,
  top: '50%',
  transform: 'translateY(-50%)',
  color: 'var(--text-muted)',
  display: 'flex',
  pointerEvents: 'none',
};

const resultsBoxStyle: CSSProperties = {
  maxHeight: 240,
  overflowY: 'auto',
  border: '1px solid var(--border)',
  borderRadius: 'var(--radius-md)',
};

const listBoxStyle: CSSProperties = {
  border: '1px solid var(--border)',
  borderRadius: 'var(--radius-md)',
  overflow: 'hidden',
};

const emptyRowStyle: CSSProperties = {
  padding: 'var(--sp-3)',
  textAlign: 'center',
  fontSize: 'var(--font-size-sm)',
  color: 'var(--text-muted)',
};

const dashedEmptyStyle: CSSProperties = {
  padding: 'var(--sp-5)',
  textAlign: 'center',
  fontSize: 'var(--font-size-sm)',
  color: 'var(--text-muted)',
  border: '1px dashed var(--border)',
  borderRadius: 'var(--radius-md)',
};

const rowTitleStyle: CSSProperties = {
  fontSize: 'var(--font-size-sm)',
  fontWeight: 'var(--font-weight-medium)',
  color: 'var(--text)',
};

const rowSubStyle: CSSProperties = {
  fontSize: 'var(--font-size-xs)',
  color: 'var(--text-muted)',
  marginTop: 1,
};

const hintStyle: CSSProperties = {
  fontSize: 'var(--font-size-xs)',
  color: 'var(--text-muted)',
};

const resultRowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 'var(--sp-3)',
  width: '100%',
  padding: 'var(--sp-2) var(--sp-3)',
  border: 'none',
  borderBottom: '1px solid var(--border)',
  background: 'var(--bg-surface)',
  cursor: 'pointer',
  textAlign: 'left',
  fontFamily: 'inherit',
};

const summaryBarStyle: CSSProperties = {
  padding: 'var(--sp-3) var(--sp-5)',
  borderBottom: '1px solid var(--border)',
  background: 'var(--bg-primary-subtle)',
  flexShrink: 0,
};

const summaryLabelStyle: CSSProperties = {
  fontSize: 'var(--font-size-xs)',
  color: 'var(--text-muted)',
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
};

const summaryValueStyle: CSSProperties = {
  fontSize: 'var(--font-size-sm)',
  fontWeight: 'var(--font-weight-semi)',
  color: 'var(--text)',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const secondaryBtnStyle: CSSProperties = {
  height: 30,
  padding: '0 12px',
  borderRadius: 'var(--radius-sm)',
  border: '1px solid var(--border)',
  background: 'var(--bg-surface)',
  color: 'var(--text)',
  fontSize: 'var(--font-size-xs)',
  fontWeight: 500,
  cursor: 'pointer',
  flexShrink: 0,
};

const primaryBtnStyle: CSSProperties = {
  height: 30,
  padding: '0 12px',
  borderRadius: 'var(--radius-sm)',
  border: 'none',
  background: 'var(--color-primary)',
  color: '#fff',
  fontSize: 'var(--font-size-xs)',
  fontWeight: 'var(--font-weight-semi)',
  cursor: 'pointer',
  flexShrink: 0,
};
